import type { Activity, JourneyData, ContentVersion } from "#shared/utils/types"
import type { CompassHistoryItem, GameHistoryItem, Metric } from "./types"

export const scrollGame = () => {
  window.setTimeout(() => {
    const gameContainer = document.querySelector("#gameContainer")
    if (gameContainer) {
      gameContainer.scrollTo({
        top: gameContainer.scrollHeight
        // behavior: "smooth"
      })
    }
  }, 100)
}

export const sortActivities = (activities: Activity[]): Activity[] => {
  return [...activities].sort((a, b) => {
    if (a.created_at < b.created_at) return -1
    if (a.created_at > b.created_at) return 1
    return 0
  })
}

export const filterActivitiesByKind = (
  kind: string,
  activities: Activity[]
): Activity[] => {
  return activities.filter((a) => a.kind === kind)
}

export const retrieveOrderedChallengesForPath = (
  pathId: number | undefined | null,
  contentVersion: ContentVersion
) => {
  if (!pathId) return []

  const quest = contentVersion.quest
  if (!quest!.paths) return []
  return quest!.paths
    .find((p: Path) => p.id === pathId)
    ?.challenges.map((cid: number) => {
      return quest!.challenges.find((c) => c.id === cid)
    })
    .filter((c): c is WddChallenge => c !== undefined) // Removes undefined values from the array
}

const findCorrespondingPopupChallenge = (
  challenge: WddChallenge | undefined,
  popupChallenges: WddChallenge[] | undefined
) => {
  if (!challenge) return undefined
  if (!popupChallenges) return undefined
  const challengePrefix = "wdd_"
  const popupChallengePrefix = "wdd_popup_"
  return popupChallenges.find(
    (c) =>
      c.name === challenge.name.replace(challengePrefix, popupChallengePrefix)
  )
}

export const findCurrentChallenge = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): {
  challenge: WddChallenge | undefined
  popupChallenge: WddChallenge | undefined
  index: number | undefined
} => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const popupChallenges = challenges?.filter((c) =>
    c.name.startsWith("wdd_popup_")
  )
  const normalChallenges = challenges?.filter(
    (c) =>
      !c.name.startsWith("wdd_popup_") && !c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )

  if (normalChallenges) {
    // we have to find the last challenge that was solved, and then find the next challenge
    const lastSolveChallengeActivity = solveChallengeActivities.pop()

    const lastSolvedChallenge = normalChallenges.find(
      (c: Challenge) => c.id === lastSolveChallengeActivity?.data.challenge_id
    )

    if (lastSolvedChallenge) {
      const currentChallengeIndex =
        normalChallenges.indexOf(lastSolvedChallenge)
      // if there is another challenge we return it
      if (normalChallenges[currentChallengeIndex + 1]) {
        const challenge = normalChallenges[currentChallengeIndex + 1]
        const popupChallenge = findCorrespondingPopupChallenge(
          challenge,
          popupChallenges
        )
        return {
          challenge: challenge,
          popupChallenge: popupChallenge,
          index: currentChallengeIndex + 1
        }
      }
      // if there is no other challenge, we return undefined (end of the game)
      return {
        challenge: undefined,
        popupChallenge: undefined,
        index: undefined
      }
    } else {
      // if we didn't find the last challenge that was solved, we return the first challenge (start of the game)
      const challenge = normalChallenges[0]
      const popupChallenge = findCorrespondingPopupChallenge(
        challenge,
        popupChallenges
      )
      return {
        challenge: challenge,
        popupChallenge: popupChallenge,
        index: 0
      }
    }
  }
  return { challenge: undefined, popupChallenge: undefined, index: undefined }
}

export const findCurrentCompassChallenge = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): {
  challenge: WddMultipleChoiceChallenge | undefined
  index: number | undefined
} => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const compassChallenges = challenges?.filter((c) =>
    c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_compass_challenge",
    sortedActivities
  )

  if (compassChallenges) {
    // we have to find the last challenge that was solved, and then find the next challenge
    const lastSolveChallengeActivity = solveChallengeActivities.pop()

    const lastSolvedChallenge = compassChallenges.find(
      (c: Challenge) => c.id === lastSolveChallengeActivity?.data.challenge_id
    )

    if (lastSolvedChallenge) {
      const currentChallengeIndex =
        compassChallenges.indexOf(lastSolvedChallenge)
      // if there is another challenge we return it
      if (compassChallenges[currentChallengeIndex + 1]) {
        const challenge = compassChallenges[currentChallengeIndex + 1]
        return {
          challenge: challenge as WddMultipleChoiceChallenge,
          index: currentChallengeIndex + 1
        }
      }
      // if there is no other challenge, we return undefined (end of the game)
      return {
        challenge: undefined,
        index: undefined
      }
    } else {
      // if we didn't find the last challenge that was solved, we return the first challenge (start of the game)
      const challenge = compassChallenges[0]
      return {
        challenge: challenge as WddMultipleChoiceChallenge,
        index: 0
      }
    }
  }
  return { challenge: undefined, index: undefined }
}

export const findSelectedGuideAndGroupName = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const guideActivities = filterActivitiesByKind(
    "select_guide",
    sortedActivities
  )

  const guideAndGroup = guideActivities.shift()
  if (!guideAndGroup) return undefined

  const guideId = guideAndGroup?.data?.guide_id as number
  const groupName = guideAndGroup?.data?.group_name as string
  const votesTopic = guideAndGroup?.data?.votes_topic as Metric

  return { guideId, groupName, votesTopic }
}

export const findCompleteGame = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const completeGameActivities = filterActivitiesByKind(
    "complete_game",
    sortedActivities
  )
  return completeGameActivities.length > 0
}

export const findCompleteBatchGame = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const completeGameActivities = filterActivitiesByKind(
    "complete_batch_game",
    sortedActivities
  )
  return completeGameActivities.length > 0
}

export const findMostRecentWddVideo = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const videoActivities = filterActivitiesByKind("wdd_video", sortedActivities)
  return videoActivities.pop()
}

export const reconstructHistory = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): GameHistoryItem[] => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const normalChallenges = challenges?.filter(
    (c) =>
      !c.name.startsWith("wdd_popup_") && !c.name.startsWith("wdd_compass_")
  )
  const popupChallenges = challenges?.filter((c) =>
    c.name.startsWith("wdd_popup_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )
  const solvePopupChallengeActivities = filterActivitiesByKind(
    "solve_popup_challenge",
    sortedActivities
  )

  if (normalChallenges && popupChallenges) {
    const history = solveChallengeActivities
      .map((activity: Activity) => {
        const challenge = normalChallenges.find(
          (c: Challenge) => c.id === activity.data.challenge_id
        )
        const popupChallenge = findCorrespondingPopupChallenge(
          challenge,
          challenges
        )
        const popupActivity = solvePopupChallengeActivities.findLast(
          (a: Activity) => a.data.challenge_id === popupChallenge?.id
        )
        return {
          challenge: challenge as WddChallenge,
          popupChallenge: popupChallenge as WddChallenge,
          activity: activity as Activity,
          popupActivity: popupActivity as Activity
        }
      })
      .filter((item) => item.challenge !== undefined)

    return history
  }

  return []
}

export const reconstructCompassHistory = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): CompassHistoryItem[] => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const compassChallenges = challenges?.filter((c) =>
    c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveCompassChallengeActivities = filterActivitiesByKind(
    "solve_compass_challenge",
    sortedActivities
  )

  if (compassChallenges) {
    const history = solveCompassChallengeActivities
      .map((activity: Activity) => {
        const challenge = compassChallenges.find(
          (c: Challenge) => c.id === activity.data.challenge_id
        )
        return {
          challenge: challenge as WddMultipleChoiceChallenge,
          activity: activity as Activity
        }
      })
      .filter((item) => item.challenge !== undefined)

    return history
  }

  return []
}

export const calculateScores = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
) => {
  const challenges: WddChallenge[] | undefined =
    retrieveOrderedChallengesForPath(journeyData.group.path_id, contentVersion)

  if (!challenges) return { klima: 0, karma: 0, kohle: 0, total: 0 }

  const getUniqueActivities = (activities: Activity[]) => {
    const recentActivitiesMap = new Map()

    activities.reverse().forEach((activity: Activity) => {
      if (!recentActivitiesMap.has(activity.data.challenge_id)) {
        recentActivitiesMap.set(activity.data.challenge_id, activity)
      }
    })

    return Array.from(recentActivitiesMap.values())
  }

  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)

  const recentSolveChallengeActivities = getUniqueActivities(
    filterActivitiesByKind("solve_challenge", sortedActivities)
  )

  const recentSolvePopupChallengeActivities = getUniqueActivities(
    filterActivitiesByKind("solve_popup_challenge", sortedActivities)
  )

  const recentSolveCompassActivities = getUniqueActivities(
    filterActivitiesByKind("solve_compass_challenge", sortedActivities)
  )

  const correctActivities = recentSolveChallengeActivities.filter(
    (a) => a.data.correct && a.data.challenge_id
  )

  const correctPopupActivities = recentSolvePopupChallengeActivities.filter(
    (a) => a.data.correct && a.data.challenge_id
  )

  const correctCompassActivities = recentSolveCompassActivities.filter(
    (a) => a.data.correct && a.data.challenge_id
  )

  const getChallengePoints = (
    challengeId: number,
    votes?: (string | undefined)[]
  ): (number | undefined)[] | number => {
    const challenge = challenges.find((c) => c.id === challengeId)

    if (!challenge) return 0

    const fragment = challenge.fragment

    if (fragment.kind === "wdd_multiple_choice_sequence") {
      const pointsArray = [
        fragment.points_1,
        fragment.points_2,
        fragment.points_3,
        fragment.points_4,
        fragment.points_5,
        fragment.points_6,
        fragment.points_7
      ]
      if (!votes) return [0, 0, 0, 0, 0, 0, 0]
      return votes.map((vote, index) => (vote ? pointsArray[index] : undefined))
    } else if (fragment.kind === "wdd_multiple_choice_conditional") {
      const pointsArray = [fragment.points, fragment.points_follow_up]
      if (!votes) return [0, 0]
      return votes.map((vote, index) => (vote ? pointsArray[index] : undefined))
    }

    return fragment.points || 0
  }

  const calculateVotes = (voteType: string) =>
    correctActivities.reduce((total: number, activity) => {
      const points = getChallengePoints(
        activity.data.challenge_id as number,
        activity.data.votes as (string | undefined)[]
      )

      if (Array.isArray(points)) {
        const filteredPoints = points
          .map((point, index) => {
            const votes = activity.data.votes as (string | undefined)[]
            return votes[index] === voteType ? point : 0
          })
          .reduce((sum: number, point) => (sum ?? 0) + (point ?? 0), 0)
        return total + filteredPoints
      } else if (activity.data.vote === voteType) {
        return total + (points ?? 0)
      }
      return total
    }, 0)

  const calculatePopupVotes = (voteType: string) =>
    correctPopupActivities.reduce((total: number, activity) => {
      const points = getChallengePoints(
        activity.data.challenge_id as number,
        activity.data.votes as (string | undefined)[]
      )

      if (Array.isArray(points)) {
        const filteredPoints = points
          .map((point, index) => {
            const votes = activity.data.votes as (string | undefined)[]
            return votes[index] === voteType ? point : 0
          })
          .reduce((sum: number, point) => (sum ?? 0) + (point ?? 0), 0)
        return total + filteredPoints
      } else if (activity.data.vote === voteType) {
        return total + (points ?? 0)
      }
      return total
    }, 0)

  const calculateCompassVotes = (voteType: string) =>
    correctCompassActivities.reduce((total: number, activity) => {
      const points = getChallengePoints(
        activity.data.challenge_id as number,
        activity.data.votes as (string | undefined)[]
      )

      if (Array.isArray(points)) {
        const filteredPoints = points
          .map((point, index) => {
            const votes = activity.data.votes as (string | undefined)[]
            return votes[index] === voteType ? point : 0
          })
          .reduce((sum: number, point) => (sum ?? 0) + (point ?? 0), 0)
        return total + filteredPoints
      } else if (activity.data.vote === voteType) {
        return total + (points ?? 0)
      }
      return total
    }, 0)

  const klimaVotes =
    calculateVotes("klima") +
    calculatePopupVotes("klima") +
    calculateCompassVotes("klima")
  const karmaVotes =
    calculateVotes("karma") +
    calculatePopupVotes("karma") +
    calculateCompassVotes("karma")
  const kohleVotes =
    calculateVotes("kohle") +
    calculatePopupVotes("kohle") +
    calculateCompassVotes("kohle")

  const total = klimaVotes + karmaVotes + kohleVotes

  return {
    klima: klimaVotes,
    karma: karmaVotes,
    kohle: kohleVotes,
    total: total
  }
}

export const findLastActivityTimestamp = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const selectGuideActivities = filterActivitiesByKind(
    "select_guide",
    sortedActivities
  )
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )
  const filteredActivities = [
    ...selectGuideActivities,
    ...solveChallengeActivities
  ]
  const lastActivity = filteredActivities.pop()
  if (!lastActivity?.created_at) return undefined
  return lastActivity?.created_at
}

export const calculateSecondsSinceTimestamp = (
  timestamp: string | undefined
) => {
  if (!timestamp) return 0
  const now = new Date()
  const activityDate = new Date(timestamp)
  if (isNaN(activityDate.getTime())) return 0
  const seconds = Math.floor((now.getTime() - activityDate.getTime()) / 1000)
  if (seconds < 0) return 0
  return seconds
}

export const heuristicallyIdentifyMapFromChallenges = (
  contentVersion: ContentVersion | undefined
): string => {
  if (!contentVersion?.quest?.challenges?.length) return "unknown"

  const prefixes = ["wdd_zurich_", "wdd_uster_", "wdd_winterthur_"]
  const cities = ["zurich", "uster", "winterthur"]

  const counts = prefixes.map((prefix: string) => {
    return contentVersion.quest!.challenges.filter((c) =>
      c.name.startsWith(prefix)
    ).length
  })

  const maxCount = Math.max(...counts)

  if (maxCount === 0) return "unknown"

  const maxIndex = counts.indexOf(maxCount)

  return cities[maxIndex] || "unknown"
}

export const calculateMapCompletion = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): boolean[] => {
  const map = heuristicallyIdentifyMapFromChallenges(contentVersion) as
    | "zurich"
    | "uster"
    | "winterthur"
    | "unknown"

  const challengeToMarkerMapping: {
    [key: string]: string[]
  } = {
    zurich: [
      "compass_zurich", // compass always remains "unsolved"
      "wdd_zurich_1",
      "wdd_zurich_2",
      "wdd_zurich_3",
      "wdd_zurich_4",
      "wdd_zurich_5",
      "wdd_zurich_6",
      "wdd_zurich_7"
    ],
    uster: [
      "compass_uster", // compass always remains "unsolved"
      "wdd_uster_1",
      "wdd_uster_2",
      "wdd_uster_3",
      "wdd_uster_4",
      "wdd_uster_5",
      "wdd_uster_6",
      "wdd_uster_7"
    ],
    winterthur: [
      "compass_winterthur", // compass always remains "unsolved"
      "wdd_winterthur_1",
      "wdd_winterthur_2",
      "wdd_winterthur_3",
      "wdd_winterthur_4",
      "wdd_winterthur_5",
      "wdd_winterthur_6",
      "wdd_winterthur_7"
    ]
  }

  const completion: boolean[] = new Array(8).fill(false)

  if (!map || map === "unknown") return completion

  const mapData = challengeToMarkerMapping[map]

  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  if (!challenges) return completion

  const sortedActivities = sortActivities(journeyData.group.activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )

  challenges.forEach((challenge) => {
    const solved = solveChallengeActivities.find(
      (a) => a.data.challenge_id === challenge.id
    )

    if (mapData && solved) {
      const index = mapData.indexOf(challenge.name)
      if (index !== -1) {
        completion[index] = true
      }
    }
  })

  return completion
}

export const calculateMaxPossibleScore = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): number => {
  const challenges: WddChallenge[] | undefined =
    retrieveOrderedChallengesForPath(journeyData.group.path_id, contentVersion)

  if (!challenges) return 0

  const getChallengeMaxPoints = (challenge: WddChallenge): number => {
    const fragment = challenge.fragment

    if (fragment.kind === "wdd_multiple_choice_sequence") {
      const pointsArray = [
        fragment.points_1 || 0,
        fragment.points_2 || 0,
        fragment.points_3 || 0,
        fragment.points_4 || 0,
        fragment.points_5 || 0,
        fragment.points_6 || 0,
        fragment.points_7 || 0
      ]
      return pointsArray.reduce((sum, points) => sum + points, 0)
    } else if (fragment.kind === "wdd_multiple_choice_conditional") {
      return (fragment.points || 0) + (fragment.points_follow_up || 0)
    }

    return fragment.points || 0
  }

  const totalMaxPoints = challenges.reduce((total, challenge) => {
    return total + getChallengeMaxPoints(challenge)
  }, 0)

  return totalMaxPoints
}

export const calculateScoresPerMetric = (
  maxScore: number,
  voteTopic: Metric
) => {
  if (!maxScore || !voteTopic) {
    return {
      klima: 0,
      karma: 0,
      kohle: 0,
      total: 0
    }
  }

  const prio1 = Math.ceil(0.3 * maxScore)
  const prio2 = Math.ceil(0.15 * maxScore)
  const prio3 = Math.ceil(0.06 * maxScore)
  const prioSum = prio1 + prio2 + prio3

  if (voteTopic === "klima") {
    return {
      klima: prio1,
      karma: prio2,
      kohle: prio3,
      total: prioSum
    }
  } else if (voteTopic === "karma") {
    return {
      klima: prio2,
      karma: prio1,
      kohle: prio3,
      total: prioSum
    }
  } else if (voteTopic === "kohle") {
    return {
      klima: prio3,
      karma: prio2,
      kohle: prio1,
      total: prioSum
    }
  } else {
    return {
      klima: 0,
      karma: 0,
      kohle: 0,
      total: 0
    }
  }
}
